import {
  useUpdateActions,
  useUpdateContext,
  useUpdatePrompt
} from "./chunk-YGDUVY2Q.mjs";
import {
  APP_UPDATER_CORE
} from "./chunk-VK56FKEY.mjs";

// src/mui/SaveOrDiscardDialog.tsx
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import { jsx, jsxs } from "react/jsx-runtime";
function CloseIcon() {
  return /* @__PURE__ */ jsx(SvgIcon, { fontSize: "small", "aria-hidden": true, children: /* @__PURE__ */ jsx("path", { d: "M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" }) });
}
function SaveOrDiscardDialog({
  open,
  headline,
  guardReasons,
  canSave = true,
  onKeepEditing,
  onDiscard,
  onCancel
}) {
  return /* @__PURE__ */ jsxs(
    Dialog,
    {
      open,
      onClose: onCancel ? () => onCancel() : void 0,
      disableEscapeKeyDown: !onCancel,
      maxWidth: "sm",
      fullWidth: true,
      slotProps: { paper: { sx: { borderRadius: "12px" } } },
      "aria-labelledby": "app-updater-save-or-discard-title",
      children: [
        /* @__PURE__ */ jsx(DialogTitle, { id: "app-updater-save-or-discard-title", sx: { fontWeight: 700, pr: onCancel ? 7 : void 0 }, children: headline }),
        onCancel && /* @__PURE__ */ jsx(
          IconButton,
          {
            "aria-label": "Close",
            onClick: onCancel,
            sx: { position: "absolute", top: (theme) => theme.spacing(1.5), right: (theme) => theme.spacing(1.5), color: "text.secondary" },
            children: /* @__PURE__ */ jsx(CloseIcon, {})
          }
        ),
        /* @__PURE__ */ jsxs(DialogContent, { children: [
          /* @__PURE__ */ jsx(Typography, { variant: "body1", color: "text.secondary", children: canSave ? "You have unsaved changes. Keep editing to save them, or discard them and reload now." : "This version of the page can't send your changes. Keep editing to copy anything you need, then reload." }),
          guardReasons.length > 0 && /* @__PURE__ */ jsx(Typography, { component: "ul", variant: "body1", sx: { mt: 1.5, mb: 0, pl: 3, fontWeight: 600 }, children: guardReasons.map((reason) => /* @__PURE__ */ jsx("li", { children: reason }, reason)) })
        ] }),
        /* @__PURE__ */ jsxs(DialogActions, { sx: { px: 3, pt: 2, pb: 3, gap: 1, "& > :not(style) ~ :not(style)": { ml: 0 } }, children: [
          /* @__PURE__ */ jsx(Button, { variant: "outlined", color: "error", onClick: onDiscard, children: "Discard and reload" }),
          /* @__PURE__ */ jsx(Box, { sx: { flex: 1 } }),
          /* @__PURE__ */ jsx(Button, { variant: "contained", color: "primary", disableElevation: true, onClick: onKeepEditing, children: "Keep editing" })
        ] })
      ]
    }
  );
}

// src/mui/UpdateToast.tsx
import Box3 from "@mui/material/Box";
import Button3 from "@mui/material/Button";
import SvgIcon2 from "@mui/material/SvgIcon";
import Typography2 from "@mui/material/Typography";
import { alpha as alpha2 } from "@mui/material/styles";

// src/mui/CountdownButton.tsx
import Box2 from "@mui/material/Box";
import Button2 from "@mui/material/Button";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
function CountdownButton({ seconds, totalSeconds, onClick }) {
  const progress = totalSeconds > 0 ? (totalSeconds - seconds) / totalSeconds * 100 : 100;
  return /* @__PURE__ */ jsxs2(
    Button2,
    {
      variant: "contained",
      color: "primary",
      onClick,
      disableElevation: true,
      sx: { position: "relative", overflow: "hidden", flex: 1, fontVariantNumeric: "tabular-nums" },
      children: [
        /* @__PURE__ */ jsx2(
          Box2,
          {
            "aria-hidden": true,
            sx: {
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: `${progress}%`,
              backgroundColor: "rgba(255, 255, 255, 0.25)",
              transition: "width 1s linear",
              "@media (prefers-reduced-motion: reduce)": { transition: "none" }
            }
          }
        ),
        /* @__PURE__ */ jsxs2(Box2, { component: "span", sx: { position: "relative", zIndex: 1 }, children: [
          "Reloading in ",
          seconds,
          "s"
        ] })
      ]
    }
  );
}

// src/mui/UpdaterSurface.tsx
import Paper from "@mui/material/Paper";
import { alpha, keyframes } from "@mui/material/styles";
import { jsx as jsx3 } from "react/jsx-runtime";
var DEFAULT_UPDATER_TOP = 80;
var GROUNDING = "#202430";
function updaterSurfaceColors(theme) {
  const nav = theme.palette.nav;
  const text = nav?.contrastText ?? "#ffffff";
  return { background: nav?.sidebar ?? GROUNDING, text, muted: alpha(text, 0.72) };
}
var enter = keyframes`
  from { opacity: 0; transform: scale(0.94); }
  to { opacity: 1; transform: none; }
`;
function UpdaterSurface({ role, top = DEFAULT_UPDATER_TOP, maxWidth, children }) {
  return /* @__PURE__ */ jsx3(
    Paper,
    {
      role,
      elevation: 0,
      sx: (theme) => {
        const colors = updaterSurfaceColors(theme);
        return {
          position: "fixed",
          top,
          left: 0,
          right: 0,
          mx: "auto",
          width: `min(${maxWidth}px, calc(100vw - ${theme.spacing(4)}))`,
          backgroundColor: colors.background,
          backgroundImage: "none",
          color: colors.text,
          borderRadius: "12px",
          boxShadow: "0 12px 32px rgba(14, 16, 21, 0.28), 0 2px 8px rgba(14, 16, 21, 0.16)",
          zIndex: theme.zIndex.snackbar,
          animation: `${enter} 250ms cubic-bezier(0, 0, 0.2, 1)`,
          "@media (prefers-reduced-motion: reduce)": { animation: "none" }
        };
      },
      children
    }
  );
}

// src/mui/UpdateToast.tsx
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var BODY = {
  available: "Reload to start using it. If you step away, the page reloads by itself.",
  guarded: "It will install after you save your changes. Nothing reloads while you have unsaved work.",
  countdown: "You've been away for a bit, so the page will reload to install it."
};
function UpdateIcon() {
  return /* @__PURE__ */ jsx4(SvgIcon2, { fontSize: "small", "aria-hidden": true, sx: { color: "info.main" }, children: /* @__PURE__ */ jsx4("path", { d: "M21 10.12h-6.78l2.74-2.82c-2.73-2.7-7.15-2.8-9.88-.1-2.73 2.71-2.73 7.08 0 9.79s7.15 2.71 9.88 0C18.32 15.65 19 14.08 19 12.1h2c0 1.98-.88 4.55-2.64 6.29-3.51 3.48-9.21 3.48-12.72 0-3.5-3.47-3.53-9.11-.02-12.58s9.14-3.47 12.65 0L21 3v7.12zM12.5 8v4.25l3.5 2.08-.72 1.21L11 13V8h1.5z" }) });
}
function UpdateToast({ phase, secondsRemaining, countdownFrom, onReloadNow, onDismiss, top }) {
  const body = BODY[phase];
  if (!body) return null;
  return /* @__PURE__ */ jsx4(UpdaterSurface, { role: "status", top, maxWidth: 400, children: /* @__PURE__ */ jsxs3(Box3, { sx: { p: 2.5 }, children: [
    /* @__PURE__ */ jsxs3(Box3, { sx: { display: "flex", alignItems: "center", gap: 1.25, mb: 0.75 }, children: [
      /* @__PURE__ */ jsx4(UpdateIcon, {}),
      /* @__PURE__ */ jsx4(Typography2, { variant: "subtitle1", component: "p", sx: { fontWeight: 700, lineHeight: 1.3, flex: 1 }, children: "A new version of the app is ready" })
    ] }),
    /* @__PURE__ */ jsx4(Typography2, { variant: "body2", sx: (theme) => ({ color: updaterSurfaceColors(theme).muted, pl: 4.25, mb: 2 }), children: body }),
    /* @__PURE__ */ jsxs3(Box3, { sx: { display: "flex", gap: 1 }, children: [
      /* @__PURE__ */ jsx4(
        Button3,
        {
          variant: "outlined",
          color: "inherit",
          onClick: onDismiss,
          sx: (theme) => ({ flex: 1, borderColor: alpha2(updaterSurfaceColors(theme).text, 0.36) }),
          children: "Later"
        }
      ),
      phase === "countdown" && secondsRemaining !== null ? /* @__PURE__ */ jsx4(CountdownButton, { seconds: secondsRemaining, totalSeconds: countdownFrom, onClick: onReloadNow }) : /* @__PURE__ */ jsx4(Button3, { variant: "contained", color: "primary", disableElevation: true, onClick: onReloadNow, sx: { flex: 1 }, children: "Reload now" })
    ] })
  ] }) });
}

// src/mui/UpdateErrorAlert.tsx
import { useState } from "react";
import Box4 from "@mui/material/Box";
import Button4 from "@mui/material/Button";
import SvgIcon3 from "@mui/material/SvgIcon";
import Typography3 from "@mui/material/Typography";
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
function RefreshIcon() {
  return /* @__PURE__ */ jsx5(SvgIcon3, { "aria-hidden": true, sx: { color: "warning.main", fontSize: 24 }, children: /* @__PURE__ */ jsx5("path", { d: "M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" }) });
}
function UpdateErrorAlert({ onReload, top, reason }) {
  const [clicked, setClicked] = useState(false);
  return /* @__PURE__ */ jsx5(UpdaterSurface, { role: "alert", top, maxWidth: 520, children: /* @__PURE__ */ jsxs4(Box4, { sx: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, px: 2.5, py: 2 }, children: [
    /* @__PURE__ */ jsx5(RefreshIcon, {}),
    /* @__PURE__ */ jsx5(Typography3, { variant: "body2", sx: { flex: "1 1 220px", fontWeight: 600 }, children: reason === "update-required" ? "This page is out of date, and a newer version isn't available yet. Your changes can't be sent. Try again in a few minutes." : "We couldn't load the latest version. Reload to try again." }),
    /* @__PURE__ */ jsx5(
      Button4,
      {
        variant: "contained",
        color: "primary",
        disableElevation: true,
        disabled: clicked,
        onClick: () => {
          if (clicked) return;
          setClicked(true);
          onReload();
        },
        children: "Reload"
      }
    )
  ] }) });
}

// src/mui/UrgentUpdateBanner.tsx
import Box5 from "@mui/material/Box";
import Button5 from "@mui/material/Button";
import SvgIcon4 from "@mui/material/SvgIcon";
import Typography4 from "@mui/material/Typography";
import { Fragment, jsx as jsx6, jsxs as jsxs5 } from "react/jsx-runtime";
function RequiredIcon() {
  return /* @__PURE__ */ jsx6(SvgIcon4, { "aria-hidden": true, sx: { color: "error.main", fontSize: 24 }, children: /* @__PURE__ */ jsx6("path", { d: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" }) });
}
function formatClock(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
function UrgentUpdateBanner({ secondsRemaining, keepEditing, onReloadNow, top }) {
  const clock = formatClock(keepEditing ? keepEditing.secondsLeft : secondsRemaining ?? 0);
  const time = /* @__PURE__ */ jsx6(Box5, { component: "span", sx: (theme) => ({ color: updaterSurfaceColors(theme).text, fontWeight: 700, fontVariantNumeric: "tabular-nums" }), children: clock });
  return /* @__PURE__ */ jsx6(UpdaterSurface, { role: "alert", top, maxWidth: 600, children: /* @__PURE__ */ jsxs5(Box5, { sx: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, px: 2.5, py: 2 }, children: [
    /* @__PURE__ */ jsx6(RequiredIcon, {}),
    /* @__PURE__ */ jsxs5(Typography4, { variant: "body2", sx: (theme) => ({ flex: "1 1 260px", color: updaterSurfaceColors(theme).muted }), children: [
      /* @__PURE__ */ jsx6(Box5, { component: "strong", sx: (theme) => ({ color: updaterSurfaceColors(theme).text, fontWeight: 700 }), children: keepEditing && !keepEditing.canSave ? "This page is out of date." : "An update is required." }),
      " ",
      keepEditing ? keepEditing.canSave ? /* @__PURE__ */ jsxs5(Fragment, { children: [
        "Save your changes and the page reloads right away. Asking again in ",
        time,
        "."
      ] }) : /* @__PURE__ */ jsxs5(Fragment, { children: [
        "Copy anything you need, then reload. Asking again in ",
        time,
        "."
      ] }) : /* @__PURE__ */ jsxs5(Fragment, { children: [
        "This page will reload in ",
        time,
        ". Save your work before then."
      ] })
    ] }),
    /* @__PURE__ */ jsx6(Button5, { variant: "contained", color: "primary", disableElevation: true, onClick: onReloadNow, children: "Reload now" })
  ] }) });
}

// src/mui/MuiUpdateToast.tsx
import { Fragment as Fragment2, jsx as jsx7, jsxs as jsxs6 } from "react/jsx-runtime";
function MuiUpdateToast({ top } = {}) {
  const ctx = useUpdateContext();
  const prompt = useUpdatePrompt();
  const actions = useUpdateActions();
  if (!ctx) return null;
  return /* @__PURE__ */ jsxs6(Fragment2, { children: [
    prompt.phase === "error" && /* @__PURE__ */ jsx7(UpdateErrorAlert, { onReload: actions.reloadNow, top, reason: prompt.error?.reason }),
    prompt.phase === "urgent" && /* @__PURE__ */ jsx7(
      UrgentUpdateBanner,
      {
        secondsRemaining: prompt.secondsToDeadline,
        keepEditing: prompt.keepEditing,
        onReloadNow: actions.reloadNow,
        top
      }
    ),
    /* @__PURE__ */ jsx7(
      UpdateToast,
      {
        phase: prompt.phase,
        secondsRemaining: prompt.secondsRemaining,
        countdownFrom: ctx.countdownSeconds,
        onReloadNow: actions.reloadNow,
        onDismiss: actions.dismiss,
        top
      }
    ),
    /* @__PURE__ */ jsx7(
      SaveOrDiscardDialog,
      {
        open: prompt.phase === "confirm",
        headline: prompt.confirm?.headline ?? "",
        guardReasons: prompt.guardReasons,
        canSave: prompt.confirm?.canSave ?? true,
        onKeepEditing: actions.keepEditing,
        onDiscard: actions.discardAndReload,
        onCancel: prompt.confirm?.cancellable ? actions.cancelConfirm : void 0
      }
    )
  ] });
}

// src/mui/index.ts
var APP_UPDATER_LAYER = "mui";
export {
  APP_UPDATER_CORE,
  APP_UPDATER_LAYER,
  CountdownButton,
  DEFAULT_UPDATER_TOP,
  MuiUpdateToast,
  SaveOrDiscardDialog,
  UpdateErrorAlert,
  UpdateToast,
  UrgentUpdateBanner
};
//# sourceMappingURL=mui.mjs.map